import { useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  MessageSquare,
  ClipboardList,
} from "lucide-react";
import { Card } from "../components/UI";
import usersApi from "../api/users";

// ── Helpers ───────────────────────────────────────────────────────────────────
function progressColor(pct) {
  if (pct >= 75)
    return {
      bar: "bg-green-500",
      text: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
    };
  if (pct >= 50)
    return {
      bar: "bg-yellow-400",
      text: "text-yellow-600",
      bg: "bg-yellow-50",
      border: "border-yellow-200",
    };
  return {
    bar: "bg-red-500",
    text: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
  };
}

function scoreLabel(pct) {
  if (pct >= 75) return "Sangat Puas";
  if (pct >= 50) return "Cukup Puas";
  return "Tidak Puas";
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse"
        >
          <div className="flex justify-between mb-3">
            <div className="h-5 bg-gray-200 rounded w-1/3" />
            <div className="h-4 bg-gray-100 rounded w-1/4" />
          </div>
          <div className="h-3 bg-gray-200 rounded-full w-full mb-2" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

// ── Result card ───────────────────────────────────────────────────────────────
function ResultCard({ result }) {
  const [open, setOpen] = useState(false);
  const pct = parseFloat(result.percentage);
  const col = progressColor(pct);
  const hasQR = result.qualitative_responses?.length > 0;

  return (
    <Card className="overflow-hidden">
      {/* Main info */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">
              {new Date(result.submitted_at).toLocaleDateString("id-ID", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <p className="text-sm font-semibold text-gray-700">
              {result.questionnaire?.title ?? "CSQ-8"}
            </p>
          </div>
          <span
            className={`text-xs font-bold px-3 py-1 rounded-full border ${col.bg} ${col.text} ${col.border}`}
          >
            {scoreLabel(pct)}
          </span>
        </div>

        {/* Score */}
        <div className="flex items-end gap-2 mb-2">
          <span className={`font-display font-900 text-3xl ${col.text}`}>
            {result.total_score}
          </span>
          <span className="text-gray-400 text-sm mb-1">
            / {result.max_score}
          </span>
          <span className={`text-sm font-semibold mb-1 ${col.text}`}>
            ({pct}%)
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-4">
          <div
            className={`h-full rounded-full transition-all ${col.bar}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Comment */}
        {result.comment && (
          <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl">
            <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600 leading-relaxed italic">
              "{result.comment}"
            </p>
          </div>
        )}
      </div>

      {/* Accordion toggle */}
      {hasQR && (
        <>
          <button
            onClick={() => setOpen(!open)}
            className="w-full flex items-center justify-between px-5 py-3 border-t border-gray-100 hover:bg-gray-50 transition-colors"
          >
            <span className="text-xs font-semibold text-primary-700">
              {open ? "Sembunyikan" : "Lihat"} jawaban kualitatif (
              {result.qualitative_responses.length} pertanyaan)
            </span>
            {open ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>

          {open && (
            <div className="border-t border-gray-100 divide-y divide-gray-50">
              {result.qualitative_responses.map((qr, i) => (
                <div key={i} className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {qr.question_number}
                    </span>
                    <div>
                      <p className="text-xs text-gray-400 mb-1 leading-relaxed">
                        {qr.question}
                      </p>
                      <p className="text-sm text-gray-800 font-medium leading-relaxed">
                        {qr.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CsqResults({ user }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await usersApi.getCsqResults(user.id);
      setResults(res.data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat data CSQ-8");
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display font-700 text-gray-900">Hasil CSQ-8</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {loading ? "..." : `${results.length} pengisian • ${user.name}`}
          </p>
        </div>
        <button
          onClick={fetchResults}
          disabled={loading}
          className="text-xs text-primary-700 hover:text-primary-900 font-semibold disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <Skeleton />
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={fetchResults}
            className="text-xs text-primary-700 font-semibold hover:underline"
          >
            Coba lagi
          </button>
        </div>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-gray-400">
          <ClipboardList className="w-12 h-12 mb-3 opacity-25" />
          <p className="text-sm font-medium text-gray-500">
            Belum ada data kepuasan
          </p>
          <p className="text-xs mt-1">User belum mengisi kuesioner CSQ-8</p>
        </div>
      ) : (
        <div className="space-y-4">
          {results.map((result) => (
            <ResultCard key={result.id} result={result} />
          ))}
        </div>
      )}
    </div>
  );
}
